<script lang="ts">
	import ProjectsFilters from '@app/components/filters/ProjectsFilters.svelte';
	import { t } from 'svelte-i18n';
	import CardsSkeletonLoader from '$lib/components/skeleton-loaders/CardsSkeletonLoader.svelte';
	import Pagination from '$lib/components/Pagination.svelte';
	import CardsGridLayout from '@app/components/cards/CardsGridLayout.svelte';
	import { goto } from '$app/navigation';
	import PageHead from '@app/components/PageHead.svelte';
	import type { InputTextProps } from '$lib/elements/inputs/types';
	import { resetFilters } from '@app/components/filters/filters';
	import { debounceAsync } from '@app/utils/global';
	import Search from '$lib/components/Search.svelte';
	import ButtonBoxSecondary from '$lib/elements/buttons/variants/ButtonBoxSecondary.svelte';
	import { navigating, page } from '$app/stores';
	import { setContext } from 'svelte';
	import { stickyAction } from '$lib/actions';
	import { urls } from '@app/urls';
	import { bannersHeight } from '@app/stores/banners';
	import { headerHeight } from '@app/stores/header';
	import ButtonBoxTextColor from '$lib/elements/buttons/variants/ButtonBoxTextColor.svelte';
	import { getOperationsFilters } from '../filters/projects-filters';
	import type { OperationRegulationsEnum } from '@app/models/api/enums/OperationStatusEnum';

	export let data;
	export let title: string;
	export let isPrivateOperations: boolean = false;
	export let regulation: OperationRegulationsEnum;

	$: filtersGroups = getOperationsFilters(regulation, $page?.url.searchParams);
	$: operations = data.operations;

	let searchQueryProps: InputTextProps = {
		placeholder: $t('projectsPage.searchPlaceholder'),
		withClearAction: false
	};

	let isStuck = false;

	function handleStuck(e) {
		isStuck = e.detail.isStuck;
	}

	const resetTextSearchValue = () => {
		searchQueryProps.value = '';
	};

	setContext('projects', {
		resetTextSearchValue: resetTextSearchValue
	});

	let onKeyUp = debounceAsync(async () => {
		let currentURL: URL = new URL(window.location.toString());
		let currentURLQuery: URLSearchParams = currentURL.searchParams;
		if (searchQueryProps && searchQueryProps.value && searchQueryProps.value !== '') {
			currentURL.search = '';
			currentURLQuery.set('search', searchQueryProps.value);
		} else {
			currentURLQuery.delete('search');
		}

		await goto(currentURL);
	}, 500);

	const onPageChange = async (e) => {
		const url = new URL(window.location.toString());
		url.searchParams.set('page', e.detail.number.toString());
		await goto(url);
	};
</script>

<PageHead title={$t('titles.projets')} />

<div class="page-stack py-5">
	<section>
		<div class="container">
			<h1 class="text-center">
				{title}
			</h1>
			<div
				use:stickyAction={{ stickToTop: true }}
				on:stuck={handleStuck}
				data-position={'top'}
				class:isStuck
				class="sticky-element bg-white z-10 pt-4 pb-3 md:pb-5 lg:pb-6 space-y-3"
				style="--banner-height:{$bannersHeight}px; --header-height:{$headerHeight}px"
			>
				<div
					class="grid grid-cols-5 md:grid-cols-2 lg:grid-cols-3 gap-3 md:items-center md:justify-between"
				>
					<p class="col-span-5 pb-0 md:col-span-1 lg:col-span-2 lg:pr-4">
						{$t('projectsPage.subtitle')}
					</p>
					<Search
						bind:props={searchQueryProps}
						on:keyup={onKeyUp}
						containerClasses="col-span-4 md:col-span-1"
					/>
					<div
						class="self-center justify-self-end md:justify-self-auto md:col-span-2 lg:col-span-3"
					>
						<ProjectsFilters
							numberOfResults={operations?.page?.totalElements ?? 0}
							{filtersGroups}
							bind:searchQueryProps
						/>
					</div>
				</div>
			</div>

			{#if $navigating && $page.url.pathname.startsWith(urls.PROJECTS.INDEX)}
				<CardsSkeletonLoader numberOfSkeletons={12} />
			{:else}
				{#if operations?.data?.length}
					<CardsGridLayout>
						{#each operations?.data as operation}
							<svelte:component this={operation.svelteComp} props={operation} />
						{/each}
					</CardsGridLayout>
				{:else}
					<div class="text-center">
						<div class="py-6 space-y-4">
							<div>{$t('noResults')}</div>
							<ButtonBoxSecondary
								props={{ label: $t('newsFeed.resetFilters') }}
								on:click={async () => {
									await resetFilters();
									resetTextSearchValue();
								}}
							/>
						</div>
					</div>
				{/if}
				{#if isPrivateOperations}
					<div class="text-center pt-6">
						<p class="w-full lg:w-1/2 text-sm lg:m-auto">
							{@html $t('privateOperationCTAHelp')}
						</p>
						<ButtonBoxTextColor
							props={{ label: $t('projectsPage.privateAccessCTALabel') }}
							on:click={async () => {
								await goto(urls.PROJECT.PRIVATE_ACCESS);
							}}
						/>
					</div>
				{/if}
				{#if operations?.data?.length && operations?.page}
					<Pagination data={operations?.page} on:load={onPageChange} />
				{/if}
			{/if}
		</div>
	</section>
</div>

<style lang="postcss">
	.sticky-element {
		@apply sticky;
		top: var(--banner-height);
		&.isStuck {
			@apply border-grey-light border-b;
		}
	}
	@media (min-height: 900px) and (min-width: 1024px) {
		.sticky-element {
			@apply sticky;
			top: calc(var(--banner-height) + var(--header-height));
		}
	}
	@media (max-height: 900px) and (min-width: 1024px) {
		.sticky-element {
			@apply relative top-0;
		}
	}
	div :global(.card) {
		@apply w-full;
	}
</style>
